import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { logEvent } from 'firebase/analytics';
import logo from './assets/blackfield-logo.svg';
import logoV from './assets/visionary-logo.svg';
import unityLogo from './assets/unity-logo.png';
import wingLeft from './assets/wing_left.svg';
import wingRight from './assets/wing_right.svg';
import bf01 from './assets/blackfield_01.webp';
import bf02 from './assets/blackfield_02.webp';
import bf03 from './assets/blackfield_03.webp';
import bf04 from './assets/blackfield_04.webp';
import bf05 from './assets/blackfield_05.webp';
import bf06 from './assets/blackfield_06.webp';
import compass from './assets/compass.svg';
import footerImg from './assets/blackfield_footer.webp';
import { getAnalyticsInstance, db } from './firebase';
import { collection, addDoc, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import { NEWS } from './news';
import './App.css';

const YOUTUBE_VIDEO_ID = 'owUpflFnaNQ';
const STEAM_URL = 'https://store.steampowered.com/app/3157920/Blackfield/';

const SLIDES = [
  {
    img: bf01,
    title: 'WORLD IS STARVING',
    desc: 'Survival-driven Narrative: Navigate a parallel universe where robots, bizarre creatures, and humans vie for scarce resources essential for survival. Marta\'s quest to find her lost family unfolds against this backdrop of intense competition and evolving alliances.',
  },
  {
    img: bf02,
    title: 'RESOURCE MANAGEMENT',
    desc: 'Strategically control limited food and energy sources. Your decisions impact relationships with factions and resource distribution, shaping Marta\'s journey and story outcomes.',
  },
  {
    img: bf03,
    title: 'DYNAMIC ALLIANCES',
    desc: 'Develop alliances or manage conflicts with robot, creature, and human groups. Influence faction dynamics through actions and choices, affecting Marta\'s path and the overarching narrative.',
  },
  {
    img: bf04,
    title: 'SURVIVAL MECHANICS',
    desc: 'Engage in survival mechanics that go beyond traditional gameplay, requiring adaptation to fluctuating environments and strategic planning to keep Marta alive.',
  },
  {
    img: bf05,
    title: 'EMOTIONAL JOURNEY',
    desc: 'Delve into Marta\'s emotional journey as she confronts loss, courage, and hope amidst a visually rich and atmospheric world filled with danger and wonder.',
  },
  {
    img: bf06,
    title: 'THE STORY',
    desc: 'In a shadowed parallel universe, where the remnants of humanity cling to existence amidst mechanical overlords and nightmarish creatures, a relentless battle for dwindling food and energy supplies rages.',
  },
];

// Infinite loop: clone last slide at index 0, clone first slide at the end
// Layout: [clone-of-last, slide0, slide1, ..., slide5, clone-of-first]
const EXT_SLIDES = [SLIDES[SLIDES.length - 1], ...SLIDES, SLIDES[0]];
const INITIAL_EXT = Math.floor(SLIDES.length / 2) + 1; // start at middle real slide


export default function App() {
  const publisherAuthed = sessionStorage.getItem('bf_gate_auth') === '1';
  // extIndex = position in EXT_SLIDES (1..SLIDES.length = real slides)
  const [extIndex, setExtIndex] = useState(INITIAL_EXT);
  const [animated, setAnimated] = useState(true);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [trailerOpen, setTrailerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [nlEmail, setNlEmail] = useState('');
  const [nlStatus, setNlStatus] = useState('idle'); // idle | loading | success | duplicate | error

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const ga = getAnalyticsInstance();
    if (ga) logEvent(ga, 'page_view', {
      page_location: window.location.href,
      page_path: window.location.pathname || '/',
    });
  }, []);

  async function handleNewsletterSubmit(e) {
    e.preventDefault();
    const email = nlEmail.trim().toLowerCase();
    if (!email) return;
    setNlStatus('loading');
    try {
      const col = collection(db, 'newsletterSignups');
      const existing = await getDocs(query(col, where('email', '==', email)));
      if (!existing.empty) {
        setNlStatus('duplicate');
        return;
      }
      await addDoc(col, { email, subscribedAt: serverTimestamp() });
      setNlStatus('success');
      setNlEmail('');
    } catch {
      setNlStatus('error');
    }
  }

  // Hold Image objects in a ref so they're never GC'd — keeps them in browser cache
  const preloadCache = useRef([]);
  useEffect(() => {
    preloadCache.current = SLIDES.map(({ img }) => {
      const image = new Image();
      image.src = img;
      return image;
    });
  }, []);

  // After an instant (no-animation) jump, re-enable animation on next frame
  useEffect(() => {
    if (!animated) {
      const raf = requestAnimationFrame(() =>
        requestAnimationFrame(() => setAnimated(true))
      );
      return () => cancelAnimationFrame(raf);
    }
  }, [animated]);

  const videoSrc = `https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}?autoplay=1&mute=1&controls=0&loop=1&playlist=${YOUTUBE_VIDEO_ID}&modestbranding=1&rel=0&playsinline=1`;

  // Desktop: slide=54vw gap=2vw → per-slide=56vw, center-offset=23vw, peek≈21vw
  // Mobile:  slide=100vw gap=0 → per-slide=100vw, center-offset=0, full-width
  const slideVw  = isMobile ? 100 : 54;
  const gapVw    = isMobile ? 0   : 2;
  const offsetVw = (100 - slideVw) / 2;
  const perSlide = slideVw + gapVw;
  const trackTransform = `translateX(calc(${offsetVw}vw - ${extIndex} * ${perSlide}vw))`;

  // Derive real slide index (0–5) from extIndex for dots and feature text
  const currentSlide = ((extIndex - 1) % SLIDES.length + SLIDES.length) % SLIDES.length;

  const prev = () => { setAnimated(true); setExtIndex(i => i - 1); };
  const next = () => { setAnimated(true); setExtIndex(i => i + 1); };

  const handleTransitionEnd = () => {
    if (extIndex === 0) {
      // Landed on clone-of-last → jump instantly to real last
      setAnimated(false);
      setExtIndex(SLIDES.length);
    } else if (extIndex === EXT_SLIDES.length - 1) {
      // Landed on clone-of-first → jump instantly to real first
      setAnimated(false);
      setExtIndex(1);
    }
  };

  const goToSlide = (i) => { setAnimated(true); setExtIndex(i + 1); };

  // Auto-scroll every 5s, pauses on hover/drag
  const autoScrollPaused = useRef(false);
  useEffect(() => {
    const interval = setInterval(() => {
      if (!autoScrollPaused.current) next();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Touch swipe support
  const touchStartX = useRef(null);
  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(delta) > 40) { delta > 0 ? next() : prev(); }
    touchStartX.current = null;
  };

  // Mouse drag support — live track movement
  const sliderTrackRef = useRef(null);
  const mouseStartX = useRef(null);
  const handleMouseDown = (e) => {
    e.preventDefault();
    mouseStartX.current = e.clientX;
    const track = sliderTrackRef.current;
    if (track) track.style.transition = 'none';

    const onMove = (ev) => {
      if (!sliderTrackRef.current || mouseStartX.current === null) return;
      const offset = ev.clientX - mouseStartX.current;
      sliderTrackRef.current.style.transform = `${trackTransform} translateX(${offset}px)`;
    };

    const onUp = (ev) => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      const delta = mouseStartX.current - ev.clientX;
      mouseStartX.current = null;
      if (sliderTrackRef.current) {
        sliderTrackRef.current.style.transition = '';
        sliderTrackRef.current.style.transform = '';
      }
      if (Math.abs(delta) > 50) { delta > 0 ? next() : prev(); }
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  return (
    <div className="app">

      <Helmet>
        <title>Blackfield – Dark Survival Adventure Game | Wishlist on Steam | Visionary Games</title>
        <meta name="description" content="Blackfield is a dark survival adventure game where 8-year-old Marta navigates a shadowed parallel universe ruled by mechanical overlords. Manage scarce resources, forge dangerous alliances, and uncover the fate of your lost family. Wishlist now on Steam." />
        <link rel="canonical" href="https://visionarygames.net/" />
        <meta property="og:title" content="Blackfield – Dark Survival Adventure | Wishlist on Steam" />
        <meta property="og:description" content="Play as 8-year-old Marta in a shadowed parallel universe ruled by mechanical overlords. Survive, scavenge, and search for your lost family in this dark atmospheric adventure. Wishlist Blackfield on Steam." />
        <meta property="og:url" content="https://visionarygames.net/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://visionarygames.net/android-chrome-512x512.png" />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Blackfield – Dark Survival Adventure | Wishlist on Steam" />
        <meta name="twitter:description" content="Play as 8-year-old Marta in a shadowed parallel universe ruled by mechanical overlords. Survive, scavenge, and search for your lost family. Wishlist Blackfield on Steam." />
        <meta name="twitter:image" content="https://visionarygames.net/android-chrome-512x512.png" />
      </Helmet>

      {/* ── Top Nav ─────────────────────────────────────── */}
      <nav className={`top-nav${scrolled ? ' top-nav-solid' : ''}`}>
        <div className="top-nav-links">
          <Link to="/" className="top-nav-link top-nav-home">HOME</Link>
          <Link to="/news" className="top-nav-link">NEWS</Link>
          <Link to="/press-kit" className="top-nav-link">PRESS KIT</Link>
          <a href="mailto:yuri@blackfieldgame.com" className="top-nav-link">CONTACT</a>
          <span className="top-nav-link top-nav-disabled">ART BOOK</span>
          <Link to="/publisher" className="top-nav-link top-nav-presentation"><span className="material-symbols-rounded" style={{fontSize:'0.85rem',verticalAlign:'middle'}}>{publisherAuthed ? 'lock_open' : 'lock'}</span> PUBLISHER</Link>
        </div>
      </nav>

      {/* ── Section 1: Hero ─────────────────────────────── */}
      <section className="hero-section">
        <div className="video-wrap">
          <iframe
            src={videoSrc}
            title="Background video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="video-iframe"
          />
        </div>

        <div className="overlay" />

        <img src={compass} alt="" className="hero-compass" aria-hidden="true" />

        <aside className="strip">
          <div className="strip-icons">
            <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" className="strip-icon" aria-hidden="true">
              <circle cx="12" cy="12" r="8.5" />
            </svg>
            <svg viewBox="0 0 24 24" fill="white" className="strip-icon" aria-hidden="true">
              <polygon points="12,3 21,12 12,21 3,12" />
            </svg>
            <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" className="strip-icon" aria-hidden="true">
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </svg>
          </div>
        </aside>

        <main className="main">
          <img src={logo} alt="BLACKFIELD" className="game-logo" />

          <p className="description">
            In a shadowed parallel universe, 8-year-old Marta navigates desolate landscapes ruled
            by mechanical overlords and nightmarish creatures, desperately seeking her lost
            family while survival becomes a relentless test of wits and willpower.
          </p>

          <div className="buttons">
            <a
              href={STEAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-steam"
            >
              <svg className="icon-steam" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.031 4.524 4.527s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.004.105.004.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.627 0 11.999-5.373 11.999-12S18.605 0 11.979 0zM7.54 18.21l-1.473-.61c.262.543.714.999 1.314 1.25 1.297.539 2.793-.076 3.332-1.375.263-.63.264-1.319.005-1.949s-.75-1.121-1.377-1.383c-.624-.26-1.29-.249-1.878-.03l1.523.63c.956.4 1.409 1.497 1.009 2.452-.397.957-1.497 1.41-2.454 1.014zm11.415-9.303c0-1.662-1.353-3.015-3.015-3.015-1.665 0-3.015 1.353-3.015 3.015 0 1.665 1.35 3.015 3.015 3.015 1.663 0 3.015-1.35 3.015-3.015zm-5.273-.005c0-1.252 1.013-2.266 2.265-2.266 1.249 0 2.266 1.014 2.266 2.266 0 1.251-1.017 2.265-2.266 2.265-1.253 0-2.265-1.014-2.265-2.265z" />
              </svg>
              <span className="btn-labels">
                <strong>WISHLIST</strong>
                <span>ON STEAM</span>
              </span>
            </a>

            <button className="btn btn-trailer" onClick={() => setTrailerOpen(true)}>
              <span className="btn-labels">
                <strong>WATCH TRAILER</strong>
              </span>
            </button>
          </div>
        </main>

        {trailerOpen && (
          <div className="trailer-modal" onClick={() => setTrailerOpen(false)}>
            <div className="trailer-modal-inner" onClick={e => e.stopPropagation()}>
              <button className="trailer-close" onClick={() => setTrailerOpen(false)}>✕</button>
              <iframe
                src={`https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}?autoplay=1&rel=0`}
                title="Blackfield Trailer"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
              />
            </div>
          </div>
        )}

        <div className="quotes-strip">
          {[
            { quote: '"DISTURBING"', source: 'MOBI' },
            { quote: '"LIMBO-ESQUE"', source: 'GDN' },
            { quote: '"HORRIFIC"', source: 'POCKETGAMER' },
          ].map(({ quote, source }) => (
            <div key={source} className="quote-item">
              <img src={wingLeft} alt="" className="wing-icon" aria-hidden="true" />
              <div className="quote-text-wrap">
                <span className="quote-title">{quote}</span>
                <span className="quote-source">{source}</span>
              </div>
              <img src={wingRight} alt="" className="wing-icon" aria-hidden="true" />
            </div>
          ))}
        </div>

        <footer className="footer">
          <img src={logoV} alt="VISIONARY GAMES" className="footer-logo" />
          <span className="footer-credit">A OSADCHY YURI GAME</span>
          <img src={unityLogo} alt="Made with Unity" className="footer-unity-img" />
        </footer>
      </section>

      {/* ── Section 2: The Game ──────────────────────────── */}
      <section className="game-section">
        <div className="section-header">
          <h2 className="section-title">THE GAME</h2>
          <p className="section-sub">World of Blackfield</p>
        </div>

        <div className="slider-outer"
          onMouseEnter={() => { autoScrollPaused.current = true; }}
          onMouseLeave={() => { autoScrollPaused.current = false; }}
          onTouchStart={() => { autoScrollPaused.current = true; }}
          onTouchEnd={() => { autoScrollPaused.current = false; }}
        >
          <div
            ref={sliderTrackRef}
            className="slider-track"
            style={{
              transform: trackTransform,
              transition: animated ? undefined : 'none',
            }}
            onTransitionEnd={handleTransitionEnd}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
          >
            {EXT_SLIDES.map((s, i) => (
              <div className="slide" key={i}>
                <img src={s.img} alt={s.title} className="slide-img" loading="eager" draggable="false" />
              </div>
            ))}
          </div>
        </div>

        <div className="slider-controls">
          <div className="slider-dots">
            {SLIDES.map((_, i) => (
              <button
                key={i}
                className={`dot${i === currentSlide ? ' dot-active' : ''}`}
                onClick={() => goToSlide(i)}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        </div>

        <div className="feature-block">
          <h3 className="feature-title">{SLIDES[currentSlide].title}</h3>
          <div className="feature-desc-row">
            <button className="slider-btn" onClick={prev} aria-label="Previous slide">
              <span className="material-symbols-rounded">chevron_left</span>
            </button>
            <p className="feature-desc">{SLIDES[currentSlide].desc}</p>
            <button className="slider-btn" onClick={next} aria-label="Next slide">
              <span className="material-symbols-rounded">chevron_right</span>
            </button>
          </div>
        </div>
      </section>

      {/* ── Section 3: News ──────────────────────────────── */}
      <section className="news-section">
        <div className="news-inner">
          <div className="news-section-header">
            <h2 className="section-title news-section-title">NEWS</h2>
            <Link to="/news" className="news-see-all">SEE ALL</Link>
          </div>

          <div className="news-grid-row">
            {NEWS.slice(0, 3).map((item) => (
              <Link key={item.slug} to={`/news/${item.slug}`} className="news-grid-card">
                <div className="news-grid-card-img-wrap">
                  <img src={item.img} alt={item.title} className="news-grid-card-img" loading="eager" />
                </div>
                <div className="news-grid-card-text">
                  <span className="news-date">{item.date}</span>
                  <h3 className="news-grid-card-title">{item.title}</h3>
                  <p className="news-grid-card-excerpt">{item.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>

        </div>
      </section>


      {/* ── Section 4: Newsletter ────────────────────────── */}
      <section className="newsletter-section">
        <div className="newsletter-inner">
          <h2 className="newsletter-title">STAY UP TO DATE WITH BLACKFIELD</h2>
          <p className="newsletter-subtitle">Sign up for our Newsletter and never miss out on the latest Blackfield news,<br />updates, and upcoming events.</p>
          {nlStatus === 'success' ? (
            <p className="newsletter-success">You're in! We'll keep you posted.</p>
          ) : (
            <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
              <input
                className="newsletter-input"
                type="email"
                placeholder="Your email address"
                value={nlEmail}
                onChange={(e) => { setNlEmail(e.target.value); setNlStatus('idle'); }}
                required
                disabled={nlStatus === 'loading'}
              />
              <button className="newsletter-btn" type="submit" disabled={nlStatus === 'loading'}>
                {nlStatus === 'loading' ? 'SUBSCRIBING…' : 'SUBSCRIBE'}
              </button>
            </form>
          )}
          {nlStatus === 'duplicate' && <p className="newsletter-error">This email is already subscribed.</p>}
          {nlStatus === 'error' && <p className="newsletter-error">Something went wrong. Please try again.</p>}
        </div>
      </section>

      {/* ── Site footer over blackfield image ────────────── */}
      <div className="site-footer-wrap">
        <img src={footerImg} alt="" className="blackfield-footer-img" />
        <div className="site-footer-overlay">
          <img src={logoV} alt="VISIONARY GAMES" className="footer-logo" />
          <span className="footer-credit">A OSADCHY YURI GAME</span>
          <img src={unityLogo} alt="Made with Unity" className="footer-unity-img" />
        </div>
      </div>

    </div>
  );
}
